// §11.5 / D-040 — the Store's first-party catalog.
//
// "A JSON file in the repo is the source of truth, seeded into SQLite at deploy. Reviewable in git,
// queryable at runtime." So the file is `catalog.json` next to this one, and this module is the
// typed door onto it — the same door the web bundle uses for a guest (§11.5: "the bundled JSON")
// and the server uses to seed §17's table at M12.
//
// **The catalog is a list, never a source of manifests.** An entry carries only what a *card*
// needs; installing still fetches `/.well-known/hangar-app.json` from the entry's origin and puts
// it through §9.2. A catalog that shipped manifests could nominate an app on an origin's behalf,
// and §9.3's whole trust model is that an origin speaks only for itself.
import catalog from './catalog.json' with { type: 'json' };
import type { Permission } from './manifest';
import type { AppIconSpec } from './icons';

export interface CatalogEntry {
  id: string;
  name: string;
  description: string;
  /**
   * §11.5 (D-203) — how this app arrives.
   *
   * `external` is the original and the default: an origin, a manifest fetched from it, §9.2
   * validation, and a §9.6 health check. `builtin` is an in-process app (§9.1) that ships inside
   * the bundle — no origin, no manifest, no health probe, because there is nothing to `HEAD` and
   * an entry that failed forever would dim its own icon to 50 %.
   *
   * This does **not** weaken §9.2's rule that an origin speaks only for itself; it scopes it. The
   * rule exists to stop the catalog nominating an app on a third party's behalf, and code that
   * shipped in this bundle has no third party to speak for.
   */
  kind?: 'external' | 'builtin';
  /**
   * Where the manifest is fetched from. The only load-bearing field for an `external` entry
   * (§9.2); absent on a `builtin` one.
   */
  origin?: string;
  category: string;
  /** §29.1 (D-369) — the sub-group header a card sits under, e.g. `'Cards'`. */
  group?: string;
  /** §9.4 — what its manifest asks for, so the card can say so *before* anything is installed. */
  permissions: Permission[];
  /**
   * D-188 — the icon to draw until the app's *own* icon arrives from its origin.
   *
   * A Store card exists before anything is installed, so there is no manifest to ask; without
   * this the four apps a visitor is invited to install would be the four abstract marks. It is
   * a hint and nothing more: a real `icons` entry in the fetched manifest wins, because §9.2's
   * rule is that an origin speaks for itself and this file does not speak for it.
   */
  icon?: AppIconSpec;
  /** §24.11 (D-200/D-205) — hidden from a signed-out visitor's Store entirely. */
  requiresAuth?: true;
  /** D-049 — a fixture app. Present in a dev or test build and nowhere else. */
  dev?: boolean;
}

const ENTRIES = catalog.apps as CatalogEntry[];

/**
 * The catalog, for a build that is or is not a development one.
 *
 * The fixtures are on `localhost` and would be dead cards in production, so `dev: false` drops
 * them. Nothing else differs: the four v1 apps (D-019, D-037) are the catalog.
 */
export function appCatalog(includeDev = false, options: CatalogView = {}): CatalogEntry[] {
  const signedIn = options.signedIn ?? true;
  return ENTRIES.filter((entry) => includeDev || entry.dev !== true)
    // §11.5 (D-205) — the eight gated games are **absent** from a guest's Store, not shown
    // locked. The guest build ships the whole file, so absent and locked cost the same to
    // implement; absent is chosen because a limited desktop that advertises what it is
    // withholding is a worse limited desktop.
    .filter((entry) => signedIn || entry.requiresAuth !== true)
    .map((entry) => ({ ...entry }));
}

/** Who is asking. Filters the catalog the same way `appCatalog`'s `dev` flag already does. */
export interface CatalogView {
  signedIn?: boolean;
}

/** True for an entry that ships inside the bundle rather than behind an origin (D-203). */
export function isBuiltinEntry(entry: CatalogEntry): boolean {
  return entry.kind === 'builtin';
}

/** The whole file, including the fixtures. For the server's deploy-time seed (M12). */
export const CATALOG_SCHEMA_VERSION: number = catalog.schema;

/**
 * §29.1 (D-369) — the Arcade's sub-group headers, in the order they are shown.
 *
 * Ordered by hand rather than alphabetically, because the order is editorial: the quiet groups
 * lead, the loud one follows, and the single-title ones are last. Both the launcher and the Store
 * read this list, which is what stops the two disagreeing about what comes first — the class of
 * mistake D-347 and D-355 both were.
 *
 * **Puzzle** joins after Boards (D-434): it is a quiet group, and Timber sits closer to a patience
 * than to anything under Action.
 */
export const ARCADE_GROUPS: readonly string[] = ['Cards', 'Boards', 'Puzzle', 'Action', 'Rhythm'];

/**
 * Sort key for a sub-group header. Known groups keep `ARCADE_GROUPS`' order; anything else follows,
 * alphabetically, rather than vanishing — an unknown group is a bug, and a bug that hides is worse.
 */
export function groupOrder(group: string | null): number {
  if (group === null) return -1;
  const index = ARCADE_GROUPS.indexOf(group);
  return index === -1 ? ARCADE_GROUPS.length : index;
}

// ── §24.13 / §29.1 (D-225, D-369) — how a list of apps becomes a list of headed sections ─────
//
// One pure function, in `shared`, because **four** surfaces render this list: three skins'
// launchers and the Store. Four independent implementations of "which header does this go under"
// is four places for the Arcade to be sorted differently, which is exactly the class of bug D-347
// and D-355 both were. The skins keep the look; the order is not a look.
//
// `shared` rather than the kernel because the Store is an app, and §6.5 forbids an app importing
// the kernel.

export interface GroupableApp {
  readonly id: string;
  readonly name: string;
  readonly category?: string;
  readonly group?: string;
}

export interface AppSection<T extends GroupableApp> {
  /** `null` for the apps that declare no category — the ones that just are. */
  readonly category: string | null;
  /** `null` for a category with no sub-groups, or for the ungrouped remainder of one. */
  readonly group: string | null;
  readonly apps: readonly T[];
}

/** Group `apps` into headed sections. Stable, total, and never drops an app. */
export function sectionsOf<T extends GroupableApp>(apps: readonly T[]): AppSection<T>[] {
  const buckets = new Map<string, { category: string | null; group: string | null; apps: T[] }>();
  for (const app of apps) {
    const category = app.category ?? null;
    const group = category === null ? null : (app.group ?? null);
    const key = `${category ?? ''} ${group ?? ''}`;
    const bucket = buckets.get(key) ?? { category, group, apps: [] };
    bucket.apps.push(app);
    buckets.set(key, bucket);
  }

  return [...buckets.values()]
    .map((bucket) => ({
      category: bucket.category,
      group: bucket.group,
      apps: [...bucket.apps].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => {
      if (a.category === null) return b.category === null ? 0 : -1;
      if (b.category === null) return 1;
      const byCategory = a.category.localeCompare(b.category);
      if (byCategory !== 0) return byCategory;
      const byGroup = groupOrder(a.group) - groupOrder(b.group);
      if (byGroup !== 0) return byGroup;
      return (a.group ?? '').localeCompare(b.group ?? '');
    });
}
